import { useForm } from '@mantine/form';
import {
  Stack,
  Box,
  Group,
  Text,
  Button,
  SimpleGrid,
  TextInput,
  NumberInput,
  Select,
  useMantineTheme,
  Divider,
} from '@mantine/core';
import {
  IconArrowRight,
  IconUpload,
  IconX,
  IconCloudUpload,
} from '@tabler/icons-react';
import { DatePickerInput } from '@mantine/dates';
import { Dropzone, DropzoneProps, PDF_MIME_TYPE } from '@mantine/dropzone';

import type {
  DisbursementFeeProcessingMethod,
  ReceiptMode,
} from '../../models/contract.ts';
import type { GasStationAccountOwnerType } from '../../models/refueling-contract.ts';
import type { Attachment, NewAttachment } from '../../models/attachment.ts';

import {
  RECEIPT_MODE_LABEL,
  REFUELING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
  GAS_STATION_ACCOUNT_OWNER_TYPE_LABEL,
  labelMapToOptionList,
} from '../../const/labels.ts';
import { notifyError } from '../../utils/NotifyError.tsx';

export interface MutateRefuelingContractFormValues {
  name: string;
  startAt: string;
  endAt: string;
  disbursementFeeProcessingMethod: DisbursementFeeProcessingMethod;
  gasStationNotificationEndpoint: string;
  gasBaseDiscount: string;
  dieselBaseDiscount: string;
  receiptMode: ReceiptMode;
  gasStationAccountOwnerType: GasStationAccountOwnerType;
  gasStationAccountThreshold?: number;
  gasStationAccountAutoDepositAmount?: number;
  existingAttachments: Attachment[];
  newAttachments: NewAttachment[];
  deletedAttachmentIds: string[];
}

export type MutateRefuelingContractFormInitialValues = Omit<
  MutateRefuelingContractFormValues,
  'newAttachments' | 'deletedAttachmentIds'
>;

interface MutateRefuelingContractFormProps {
  initialValues: MutateRefuelingContractFormInitialValues;
  onSubmit: (formValues: MutateRefuelingContractFormValues) => void;
}

const dropZoneProps: Partial<DropzoneProps> = {
  accept: PDF_MIME_TYPE,
  maxSize: 30000000, // 30MB
};
const receiptModeSelections = labelMapToOptionList(RECEIPT_MODE_LABEL);
const disbursementFeeProcessingMethodSelections = labelMapToOptionList(
  REFUELING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
);
const gasStationAccountOwnerTypeSelections = labelMapToOptionList(
  GAS_STATION_ACCOUNT_OWNER_TYPE_LABEL,
);

export default function MutateRefuelingContractForm({
  initialValues,
  onSubmit,
}: MutateRefuelingContractFormProps) {
  // hooks here
  const theme = useMantineTheme();
  const form = useForm<MutateRefuelingContractFormValues>({
    mode: 'controlled',
    initialValues: {
      newAttachments: [],
      deletedAttachmentIds: [],
      ...initialValues,
    },
    validate: {
      name: (value) => (value ? null : ' '),
      startAt: (value, values) =>
        value && values.endAt && new Date(value) <= new Date(values.endAt)
          ? null
          : ' ',
      endAt: (value, values) =>
        value && values.startAt && new Date(values.startAt) <= new Date(value)
          ? null
          : ' ',
      gasBaseDiscount: (value) =>
        value && 0 <= parseFloat(value) ? null : ' ',
      dieselBaseDiscount: (value) =>
        value && 0 <= parseFloat(value) ? null : ' ',
      newAttachments: (value, values) => {
        if (value.length > 1) {
          return '只能上傳一份檔案';
        }
        if (
          values.existingAttachments.length +
            value.length -
            values.deletedAttachmentIds.length <
          1
        ) {
          return '附件不可為空';
        }
        return null;
      },
      gasStationAccountThreshold: (value, values) =>
        values.gasStationAccountOwnerType !== 'JustLog' && !value ? ' ' : null,
      gasStationAccountAutoDepositAmount: (value, values) =>
        values.gasStationAccountOwnerType !== 'JustLog' && !value ? ' ' : null,
    },
  });

  const displayAttachments: (Attachment | NewAttachment)[] = [
    ...form.values.existingAttachments.filter(
      (attachment) => !form.values.deletedAttachmentIds.includes(attachment.id),
    ),
    ...form.values.newAttachments,
  ];

  // callback function
  function onDrop(files: File[]): void {
    const attachments = files.map((file) => {
      return {
        displayName: file.name,
        file: file,
      };
    });
    form.setValues((prev) => ({
      ...prev,
      newAttachments: [...prev.newAttachments, ...attachments],
    }));
  }
  function onClickDeleteFile(
    deleteAttachment: Attachment | NewAttachment,
  ): void {
    const isExistingAttachment = 'id' in deleteAttachment;

    // 如果是刪除已存在的檔案，就放進待刪除佇列；反之，從暫存區移除掉即可
    if (isExistingAttachment) {
      form.setValues((prev) => ({
        ...prev,
        deletedAttachmentIds: [
          ...prev.deletedAttachmentIds,
          deleteAttachment.id,
        ],
      }));
    } else {
      form.setValues((prev) => ({
        ...prev,
        newAttachments: prev.newAttachments.filter(
          (attachment) => attachment !== deleteAttachment,
        ),
      }));
    }
  }

  return (
    <form
      id="mutateRefuelingContractForm"
      onSubmit={form.onSubmit(
        (values: MutateRefuelingContractFormValues) => onSubmit(values),
        () => {
          notifyError('合約儲存失敗', '尚有欄位未填寫完成或格式錯誤');
        },
      )}
    >
      <Text size="sm" fw={500} c="gray" mt={40}>
        基本設定
      </Text>
      <Divider mt={10} mb={20} />
      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
        <TextInput
          withAsterisk
          label="合約名稱"
          placeholder="請輸入合約名稱"
          key={form.key('name')}
          {...form.getInputProps('name')}
        />
        <Group>
          <DatePickerInput
            withAsterisk
            label="合約生效起迄日"
            placeholder="請輸入合約生效起始日"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            key={form.key('startAt')}
            {...form.getInputProps('startAt')}
          />
          <Stack>
            <Box h={12} /> {/* 塞個東東讓下面的箭頭位置正確 */}
            <IconArrowRight />
          </Stack>
          <DatePickerInput
            label=" "
            placeholder="請輸入合約生效結束日"
            style={{ flex: 1 }}
            valueFormat="YYYY/MM/DD"
            key={form.key('endAt')}
            {...form.getInputProps('endAt')}
          />
        </Group>
        <Select
          allowDeselect={false}
          label="手續費扣除方式"
          data={disbursementFeeProcessingMethodSelections}
          key={form.key('disbursementFeeProcessingMethod')}
          {...form.getInputProps('disbursementFeeProcessingMethod')}
        />
        <TextInput
          label="加油站交易通知網址"
          placeholder="請輸入加油站交易通知網址"
          key={form.key('gasStationNotificationEndpoint')}
          {...form.getInputProps('gasStationNotificationEndpoint')}
        />
        <Select
          allowDeselect={false}
          label="發票開立方式"
          data={receiptModeSelections}
          defaultValue="DEFAULT"
          key={form.key('receiptMode')}
          {...form.getInputProps('receiptMode')}
        />
      </SimpleGrid>

      <Text size="sm" fw={500} c="gray" mt={60}>
        分潤設定
      </Text>
      <Divider mt={10} mb={20} />
      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
        <NumberInput
          withAsterisk
          label="汽油分潤條件"
          placeholder="請輸入汽油分潤條件（公升/元）"
          min={0}
          decimalScale={3}
          key={form.key('gasBaseDiscount')}
          {...form.getInputProps('gasBaseDiscount')}
        />
        <NumberInput
          withAsterisk
          label="柴油分潤條件"
          placeholder="請輸入柴油分潤條件（公升/元）"
          min={0}
          decimalScale={3}
          key={form.key('dieselBaseDiscount')}
          {...form.getInputProps('dieselBaseDiscount')}
        />
      </SimpleGrid>

      <Text size="sm" fw={500} c="gray" mt={60}>
        現銷金設定
      </Text>
      <Divider mt={10} mb={20} />
      <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
        <Select
          allowDeselect={false}
          label="現銷金帳戶類型"
          data={gasStationAccountOwnerTypeSelections}
          key={form.key('gasStationAccountOwnerType')}
          {...form.getInputProps('gasStationAccountOwnerType')}
        />
        {form.values.gasStationAccountOwnerType !== 'JustLog' && (
          <>
            <NumberInput
              withAsterisk
              label="自動補款門檻"
              placeholder="請輸入自動補款門檻（元）"
              min={0}
              key={form.key('gasStationAccountThreshold')}
              {...form.getInputProps('gasStationAccountThreshold')}
            />
            <NumberInput
              withAsterisk
              label="自動補款金額"
              placeholder="請輸入自動補款金額（元）"
              min={0}
              key={form.key('gasStationAccountAutoDepositAmount')}
              {...form.getInputProps('gasStationAccountAutoDepositAmount')}
            />
          </>
        )}
      </SimpleGrid>

      <Text size="sm" fw={500} c="gray" mt={60} mb={20}>
        合約上傳
      </Text>
      {displayAttachments.length === 0 && (
        <>
          <Dropzone
            onDrop={(files) => onDrop(files)}
            onReject={() => alert('只可上傳 < 30MB 的 PDF 檔案！')}
            maxSize={5 * 1024 ** 2}
            accept={PDF_MIME_TYPE}
            {...dropZoneProps}
            p="xl"
            style={{
              border: form.errors.newAttachments
                ? '2px dashed var(--mantine-color-red-6 )'
                : '2px dashed var(--mantine-color-gray-2 )',
            }}
          >
            <Group
              justify="center"
              gap="xl"
              mih={220}
              style={{ cursor: 'pointer' }}
            >
              <Dropzone.Accept>
                <IconUpload
                  size={52}
                  color={theme.colors.blue[6]}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={52} color={theme.colors.red[6]} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconCloudUpload size={52} color="#212529" stroke={1.5} />
              </Dropzone.Idle>

              <div>
                <Text size="xl" inline>
                  上傳加油站紙本合約
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  把檔案拖曳到這裡上傳。只接受小於 30MB 的 .pdf 檔案。
                </Text>
              </div>
            </Group>
          </Dropzone>
        </>
      )}
      {displayAttachments.length > 0 && (
        <>
          <Text size="sm" fw={500} inline mb={10}>
            已選擇檔案：
          </Text>
          {displayAttachments.map((attachment) => {
            return (
              <Group justify="space-between">
                <Text size="sm" fw={500}>
                  {attachment.displayName}
                </Text>
                <Button
                  variant="transparent"
                  color="red"
                  onClick={() => onClickDeleteFile(attachment)}
                >
                  <Text size="sm" fw={500} td="underline">
                    刪除檔案
                  </Text>
                </Button>
              </Group>
            );
          })}
        </>
      )}

      {form.errors.newAttachments && (
        <Text c="red" fz="xs" mt={4}>
          {form.errors.newAttachments}
        </Text>
      )}
    </form>
  );
}
